import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { useMarkDialogDirty } from "../Dialog/FormDirtyContext";

interface RadioGroupOption {
  label: string;
  value: string;
  /**
   * Supporting copy shown beneath the label. Only rendered when the group is
   * `vertical` — a horizontal row of choices has no room for it.
   */
  description?: string;
}

interface RadioGroupProps {
  value: string;
  onChange: (value: string) => void;
  options: RadioGroupOption[];
  /**
   * Stack the choices instead of laying them out in a row. Use when the
   * options carry descriptions, or when there are enough of them that a row
   * would wrap (e.g. picking one NMFC sub-item from a catalogue).
   */
  vertical?: boolean;
}

export function RadioGroup({
  value,
  onChange,
  options,
  vertical,
}: RadioGroupProps) {
  const markDirty = useMarkDialogDirty();

  return (
    <RadioGroupPrimitive.Root
      value={value}
      onValueChange={(v) => {
        markDirty?.();
        onChange(v);
      }}
      className={vertical ? "flex flex-col gap-1" : "flex items-center gap-5"}
    >
      {options.map((opt) => (
        <label
          key={opt.value}
          className={
            vertical
              ? "flex cursor-pointer items-start gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-foreground hover:bg-accent has-data-[state=checked]:bg-accent"
              : "flex cursor-pointer items-center gap-2 text-sm font-medium text-foreground"
          }
        >
          <RadioGroupPrimitive.Item
            value={opt.value}
            // Vertical rows are top-aligned so the control tracks the label's
            // first line rather than centring against a tall description.
            className={`h-4 w-4 shrink-0 rounded-full border-2 border-foreground/30 data-[state=checked]:border-brand ${vertical ? "mt-0.5" : ""}`}
          >
            <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
              <span className="block h-2 w-2 rounded-full bg-brand" />
            </RadioGroupPrimitive.Indicator>
          </RadioGroupPrimitive.Item>
          {vertical ? (
            <span className="flex flex-col gap-0.5">
              <span>{opt.label}</span>
              {opt.description !== undefined && (
                <span className="font-normal text-content-secondary">
                  {opt.description}
                </span>
              )}
            </span>
          ) : (
            opt.label
          )}
        </label>
      ))}
    </RadioGroupPrimitive.Root>
  );
}
